import React, { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { PhoneCall, Menu, X } from "lucide-react";
import { WA_BASE_URL } from "../../mock";

const MainLayout = () => {
  const [scrolled, setScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  /* =========================
     SCROLL EFFECT
  ========================= */
  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY || window.pageYOffset;
      setScrolled(y > 16);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* =========================
     WHATSAPP CTA
  ========================= */
  const handleHeaderCTA = () => {
    const url = `${WA_BASE_URL}?text=${encodeURIComponent(
      "Halo, saya mau tanya dan pesan bumbu CINEURASA."
    )}`;
    window.open(url, "_blank");
  };

  /* =========================
     NAVIGATION LINKS
  ========================= */
  const navLinks = [
    { name: "Produk", href: "#produk" },
    { name: "Kenapa CINEURASA", href: "#keunggulan" },
    { name: "Testimoni", href: "#testimoni" },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* =========================
          HEADER
      ========================= */}
      <header
        className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${
          scrolled
            ? "bg-background/90 backdrop-blur-md shadow-sm border-border py-2"
            : "bg-transparent border-transparent py-4"
        }`}
      >
        <div className="flex items-center justify-between max-w-6xl px-4 mx-auto md:px-6">
          {/* LOGO */}
          <a
            href="/"
            className="relative block w-auto transition-transform h-14 shrink-0 hover:scale-105"
          >
            <img
              src="/assets/img/logo/logo-cineurasa.png"
              alt="Cineurasa Logo"
              className="object-contain w-auto h-full drop-shadow-sm"
            />
          </a>

          {/* DESKTOP NAV */}
          <nav className="items-center hidden gap-8 md:flex">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-sm font-semibold transition-colors text-muted-foreground hover:text-primary"
              >
                {link.name}
              </a>
            ))}
          </nav>

          {/* ACTIONS */}
          <div className="flex items-center gap-3">
            {/* CTA */}
            <Button
              size="sm"
              onClick={handleHeaderCTA}
              className="items-center hidden gap-2 px-4 py-2 text-xs font-semibold transition rounded-full shadow-md xs:inline-flex bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 md:text-sm"
            >
              <PhoneCall className="w-4 h-4" />
              <span className="hidden sm:inline">Chat WhatsApp</span>
              <span className="sm:hidden">Chat</span>
            </Button>

            {/* MOBILE MENU TOGGLE */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 transition rounded-md text-muted-foreground hover:text-primary focus:outline-none focus:ring-2 focus:ring-ring md:hidden"
              aria-label="Toggle Menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* =========================
            MOBILE MENU
        ========================= */}
        <div
          className={`absolute left-0 top-full w-full overflow-hidden border-b border-border bg-background/95 backdrop-blur-md shadow-lg transition-all duration-300 md:hidden ${
            isMobileMenuOpen ? "max-h-64 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          <div className="flex flex-col px-4 py-4 space-y-4">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={() => setIsMobileMenuOpen(false)}
                className="rounded-md px-2 py-1.5 text-base font-medium text-muted-foreground transition hover:bg-secondary hover:text-primary"
              >
                {link.name}
              </a>
            ))}

            {/* CTA MOBILE */}
            <div className="pt-2 border-t border-border sm:hidden">
              <Button
                onClick={handleHeaderCTA}
                className="flex items-center justify-center w-full gap-2 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <PhoneCall className="w-4 h-4" />
                Chat WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* =========================
          MAIN CONTENT
      ========================= */}
      <main className="max-w-6xl px-4 pt-6 pb-16 mx-auto md:px-6 md:pt-10">
        <Outlet />
      </main>

      {/* =========================
          FOOTER
      ========================= */}
      <footer className="border-t border-border bg-background/60">
        <div className="flex flex-col max-w-6xl gap-4 px-4 py-6 mx-auto text-sm text-muted-foreground md:flex-row md:items-center md:justify-between md:px-6">
          <p>
            © {new Date().getFullYear()}{" "}
            <span className="font-semibold text-primary">CINEURASA</span>. Rasa
            Aceh autentik.
          </p>

          <div className="flex items-center gap-2 px-3 py-1 border rounded-full border-border bg-secondary">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex w-full h-full rounded-full opacity-75 animate-ping bg-primary"></span>
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-primary"></span>
            </span>
            <span className="text-xs font-medium text-foreground">
              Fast response via WhatsApp
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MainLayout;
